/**
 * OnPoint V2 — Shared Pattern Library
 * The analytical evidence layer (the design's strongest device) + reusable
 * memorandum primitives. Analytical graphics are ILLUSTRATIVE of the method
 * named in the locked copy — never fabricated client metrics or outcomes.
 */
import { Link } from 'react-router-dom';

/* ── Analytical evidence figures (SVG, paper-system, mono-annotated) ── */

export const FigureIntervalResidual = () => (
  <figure className="relative" aria-label="Interval residual trace illustrating residual bias, burst clustering and tail volatility">
    <svg viewBox="0 0 520 170" role="img" className="w-full h-auto" aria-hidden="true">
      <line x1="8" y1="85" x2="512" y2="85" stroke="#C9CFC4" strokeWidth="1" />
      <text x="512" y="98" textAnchor="end" fontFamily="var(--font-mono)" fontSize="10" fill="#948F82">t</text>
      <text x="8" y="55" fontFamily="var(--font-mono)" fontSize="10" fill="#948F82">residual</text>
      <polyline points="8,60 60,64 110,58 160,70 210,120 236,132 262,124 312,96 362,88 412,84 460,80 512,78"
        fill="none" stroke="#163B38" strokeWidth="2" />
      <rect x="202" y="40" width="78" height="92" fill="none" stroke="#0F6E5A" strokeDasharray="3 3" />
      <text x="241" y="155" textAnchor="middle" fontFamily="var(--font-mono)" fontSize="10" fill="#0F6E5A">burst clustering</text>
      <line x1="128" y1="52" x2="246" y2="120" stroke="#C0533B" strokeDasharray="2 2" strokeWidth="1.2" />
      <text x="128" y="44" textAnchor="middle" fontFamily="var(--font-mono)" fontSize="9" fill="#9A4A33">tail volatility</text>
      <circle cx="70" cy="63" r="3" fill="#163B38" /><circle cx="360" cy="89" r="3" fill="#C0533B" />
    </svg>
    <figcaption className="marginal-note mt-2">
      Interval residual trace. Aggregates mask what intervals reveal — burst clustering and tail volatility are the diagnosis in the data.
    </figcaption>
  </figure>
);

export const FigureStressWindows = () => (
  <figure className="relative" aria-label="Recurring stress windows across intervals while the aggregate remains flat">
    <svg viewBox="0 0 520 170" role="img" className="w-full h-auto" aria-hidden="true">
      <line x1="8" y1="30" x2="8" y2="140" stroke="#C9CFC4" strokeWidth="1" />
      <text x="14" y="104" fontFamily="var(--font-mono)" fontSize="9" fill="#948F82" transform="rotate(-90 14 104)">interval</text>
      <rect x="58" y="20" width="20" height="118" fill="#DDE4DE" /><rect x="104" y="34" width="20" height="104" fill="#DDE4DE" />
      <rect x="150" y="14" width="20" height="124" fill="#DDE4DE" /><rect x="196" y="48" width="20" height="90" fill="#DDE4DE" />
      <rect x="242" y="70" width="20" height="68" fill="#DDE4DE" /><rect x="288" y="26" width="20" height="112" fill="#DDE4DE" />
      <rect x="334" y="58" width="20" height="80" fill="#DDE4DE" /><rect x="380" y="40" width="20" height="98" fill="#DDE4DE" />
      <rect x="426" y="18" width="20" height="120" fill="#DDE4DE" />
      <rect x="150" y="14" width="20" height="124" fill="none" stroke="#C0533B" strokeWidth="1.6" />
      <rect x="426" y="18" width="20" height="120" fill="none" stroke="#C0533B" strokeWidth="1.6" />
      <text x="160" y="160" textAnchor="middle" fontFamily="var(--font-mono)" fontSize="9" fill="#9A4A33">stress</text>
      <text x="436" y="160" textAnchor="middle" fontFamily="var(--font-mono)" fontSize="9" fill="#9A4A33">stress</text>
      <path d="M20,150 C100,140 200,146 300,150 C380,153 460,147 512,150" fill="none" stroke="#948F82" strokeDasharray="2 2" />
      <text x="512" y="142" textAnchor="end" fontFamily="var(--font-mono)" fontSize="9" fill="#948F82">aggregate: flat</text>
    </svg>
    <figcaption className="marginal-note mt-2">
      Recurring stress windows recur across intervals while the aggregate remains flat — the pattern aggregates conceal.
    </figcaption>
  </figure>
);

export const FigureCoverageGeometry = () => (
  <figure className="relative" aria-label="Coverage geometry: demand shape versus scheduled coverage, with shift boundary marker">
    <svg viewBox="0 0 520 170" role="img" className="w-full h-auto" aria-hidden="true">
      <line x1="8" y1="30" x2="8" y2="140" stroke="#C9CFC4" strokeWidth="1" />
      <path d="M8,40 C60,34 110,90 160,120 C210,150 260,60 320,70 C380,78 430,110 512,120" fill="none" stroke="#163B38" strokeWidth="2.2" />
      <path d="M8,52 C60,58 130,98 200,116 C270,134 330,88 420,92 C470,94 500,102 512,104" fill="none" stroke="#C0533B" strokeWidth="1.6" strokeDasharray="4 3" />
      <text x="56" y="46" fontFamily="var(--font-mono)" fontSize="9" fill="#163B38">demand (actual shape)</text>
      <text x="56" y="150" fontFamily="var(--font-mono)" fontSize="9" fill="#9A4A33">coverage (scheduled geometry)</text>
      <line x1="180" y1="0" x2="180" y2="168" stroke="#0F6E5A" strokeDasharray="3 3" />
      <text x="180" y="14" textAnchor="middle" fontFamily="var(--font-mono)" fontSize="9" fill="#0F6E5A">shift boundary</text>
    </svg>
    <figcaption className="marginal-note mt-2">
      Demand shape versus scheduled coverage geometry at interval level — coverage mismatch and shift boundary fragility.
    </figcaption>
  </figure>
);

/* The evidence figure rendered inside a memorandum "plate" (bordered, tagged) */
export const EvidencePlate = ({ tag, children }) => (
  <div className="border border-memo-line bg-memo-paper">
    <div className="px-4 py-2 border-b border-memo-line">
      <span className="font-mono text-xs tracking-[0.2em] uppercase text-memo-signal">{tag}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

/* ── Memorandum primitives ── */

export const MemoEyebrow = ({ children, className }) => (
  <p className={`font-body text-[0.82rem] uppercase tracking-[0.14em] text-memo-ink-2 font-medium ${className || ''}`}>{children}</p>
);

/* Hairline-bordered memorandum frame (document-grade, non-pill) */
export const MemoFrame = ({ children, className }) => (
  <div className={`border border-memo-line-2 bg-memo-paper ${className || ''}`}>{children}</div>
);

/* Section number (marginal) */
export const SectionNum = ({ children }) => (
  <span className="font-mono text-sm text-memo-signal tracking-[0.18em]">{children}</span>
);

/* Rule divider */
export const Rule = ({ strong }) => (
  <div aria-hidden="true" className={strong ? 'border-t border-memo-line-2' : 'border-t border-memo-line'} />
);

/* Primary ink-on-paper CTA (no arrow, hairline-disciplined) */
export const CtaPrimary = ({ to, children, className, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`inline-flex items-center justify-center px-6 py-3 text-[0.92rem] font-medium bg-memo-ink text-memo-paper hover:bg-memo-signal transition-colors duration-150 rounded ${className || ''}`}
  >
    {children}
  </Link>
);

export const CtaGhost = ({ to, children, className, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`inline-flex items-center justify-center px-6 py-3 text-[0.92rem] font-medium border border-memo-line-2 text-memo-ink hover:border-memo-accent hover:text-memo-accent bg-transparent transition-colors duration-150 rounded ${className || ''}`}
  >
    {children}
  </Link>
);

/* Trust chip (paper-ruled, non-pill) */
export const TrustChip = ({ icon: Icon, label }) => (
  <div className="inline-flex items-center gap-2 border border-memo-line bg-memo-paper px-3 py-2 rounded">
    {Icon && <Icon size={14} className="text-memo-signal" />}
    <span className="text-[0.82rem] text-memo-ink-2">{label}</span>
  </div>
);

export const memoPatterns = (memo) => ('');